import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import TechniqueForm from "@/components/TechniqueForm";
import AddToLibraryDialog from "@/components/AddToLibraryDialog";
import { ScopeBadge } from "@/components/ScopeBadge";
import { POSITIONS, CATEGORIES, CATEGORY_COLORS, BELT_COLORS } from "@/lib/constants";
import { resolveVideo, getThumbnail } from "@/lib/video";
import { Plus, Search, Play, Pencil, Trash2, BookmarkPlus, Filter } from "lucide-react";
import { toast } from "sonner";

const ALL = "__all__";

export default function Library() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [position, setPosition] = useState(ALL);
  const [category, setCategory] = useState(ALL);
  const [scope, setScope] = useState(ALL);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [addItem, setAddItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (position !== ALL) params.position = position;
      if (category !== ALL) params.category = category;
      if (scope !== ALL) params.scope = scope;
      const { data } = await api.get("/techniques", { params });
      setItems(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [q, position, category, scope]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const isPA = user?.role === "platform_admin";
  const canEdit = (t) => t.scope === "platform" ? isPA
    : isPA ? false
    : t.scope === "personal" ? t.owner_id === user?.user_id
    : (user?.role === "admin" || user?.role === "coach") && t.gym_id === user?.gym_id;

  const doDelete = async () => {
    try { await api.delete(`/techniques/${deleteId}`); toast.success("Deleted"); setDeleteId(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 md:p-8 pb-24 md:pb-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-1">Library</p>
          <h1 className="font-heading font-bold text-3xl tracking-tight">Techniques</h1>
        </div>
        <Button data-testid="add-technique-btn" onClick={() => { setEditing(null); setFormOpen(true); }}
          className="bg-primary hover:bg-primary/90 text-white rounded">
          <Plus className="h-4 w-4 mr-1.5" /> Add technique
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="technique-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search techniques, tags…" className="pl-9 bg-card border-border" />
        </div>
        <Select value={position} onValueChange={setPosition}>
          <SelectTrigger data-testid="filter-position" className="w-[150px] bg-card border-border"><SelectValue placeholder="Position" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All positions</SelectItem>{POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="filter-category" className="w-[150px] bg-card border-border"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All types</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger data-testid="filter-scope" className="w-[150px] bg-card border-border"><SelectValue placeholder="Owner" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All owners</SelectItem>
            <SelectItem value="gym">🏫 Gym</SelectItem>
            <SelectItem value="personal">👤 Personal</SelectItem>
            <SelectItem value="platform">🌐 Platform</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-64 rounded-xl bg-card border border-border animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Filter className="h-10 w-10 text-muted-foreground mb-4" />
          <h3 className="font-heading text-xl font-semibold">No techniques found</h3>
          <p className="text-muted-foreground mt-1 mb-5 text-sm">Start building your library — add your first move.</p>
          <Button data-testid="empty-add-technique-btn" onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-primary text-white"><Plus className="h-4 w-4 mr-1.5" /> Add technique</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((t) => {
            const v = resolveVideo(t);
            return (
              <div key={t.id} data-testid={`technique-card-${t.id}`}
                className="group rounded-xl bg-card border border-border hover:border-primary/60 transition-colors duration-300 overflow-hidden flex flex-col">
                <div className="relative aspect-video bg-black cursor-pointer overflow-hidden" onClick={() => navigate(`/techniques/${t.id}`)}>
                  <img src={getThumbnail(t)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-11 w-11 rounded-full bg-primary/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="h-5 w-5 text-white fill-white ml-0.5" />
                    </div>
                  </div>
                  <ScopeBadge scope={t.scope} className="absolute top-2 left-2 bg-black/70 text-white" />
                  {v.type !== "none" && <span className="absolute bottom-2 right-2 text-[10px] px-1.5 py-0.5 rounded bg-black/70 text-emerald-400">video</span>}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {t.category && <span className="text-[10px] font-semibold px-2 py-0.5 rounded" style={{ background: `${CATEGORY_COLORS[t.category]}22`, color: CATEGORY_COLORS[t.category] }}>{t.category}</span>}
                    {t.belt_level && <span className="text-[10px] px-2 py-0.5 rounded border border-border flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: BELT_COLORS[t.belt_level] }} />{t.belt_level}</span>}
                  </div>
                  <h3 className="font-heading font-semibold text-base leading-tight cursor-pointer hover:text-primary" onClick={() => navigate(`/techniques/${t.id}`)}>{t.name}</h3>
                  {t.position && <p className="text-xs text-muted-foreground mt-1">{t.position}</p>}
                  {t.scope === "platform" && !isPA ? (
                    <div className="flex gap-1 mt-3 pt-3 border-t border-border">
                      <Button data-testid={`add-to-library-${t.id}`} variant="ghost" size="sm" onClick={() => setAddItem(t)}
                        className="text-primary hover:text-primary hover:bg-primary/10 h-10 sm:h-8 px-2">
                        <BookmarkPlus className="h-3.5 w-3.5 mr-1" /> Add to Library
                      </Button>
                    </div>
                  ) : canEdit(t) && (
                    <div className="flex gap-1 mt-3 pt-3 border-t border-border">
                      <Button data-testid={`edit-technique-${t.id}`} variant="ghost" size="sm" onClick={() => { setEditing(t); setFormOpen(true); }} className="text-muted-foreground hover:text-white h-10 sm:h-8 px-2"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                      <Button data-testid={`delete-technique-${t.id}`} variant="ghost" size="sm" onClick={() => setDeleteId(t.id)} className="text-muted-foreground hover:text-destructive h-10 sm:h-8 px-2"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TechniqueForm open={formOpen} onOpenChange={setFormOpen} technique={editing} onSaved={load} />
      <AddToLibraryDialog open={!!addItem} onOpenChange={(o) => !o && setAddItem(null)} item={addItem} kind="technique" onDone={load} />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete technique?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone. The technique will be removed from your library.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-technique" onClick={doDelete} className="bg-destructive hover:bg-destructive/90 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
